import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {defer, Observable, of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {handleResponseError} from '../../../common/api-helpers';
import {createPendingRequest, PendingRequest} from '../../../common/request-synchronization';
import {Exchange, EXCHANGE_NAMING, SMART_ACCOUNT_EXCHANGE_API_VALUE} from '../../../enums/exchange';
import {AppConfigService} from '../../../services/config.service';
import {ExchangeRec} from './exchange-rec';

@Injectable()
export class ExchangesService {

    private readonly exchangesUrl = this.appConfigService.api['exchanges'];

    private activeExchangesCache: Exchange[];

    private pendingExchangesResolving: PendingRequest<Exchange[]>;

    constructor(
        private appConfigService: AppConfigService,
        private httpClient: HttpClient
    ) {
        this.resolveActiveExchanges().pipe(
            takeUntilDestroyed()
        ).subscribe();
    }

    public isExchangesApiAvailable(): boolean {
        return !!this.exchangesUrl;
    }

    public resolveActiveExchanges(): Observable<Exchange[]> {
        const getPendingRequest = () => {
            const request$ = defer(() =>
                this.fetchExchangeRecs().pipe(
                    map(() => this.activeExchangesCache)
                )
            );

            return this.pendingExchangesResolving
                || createPendingRequest(
                    request$,
                    newPendingReq => this.pendingExchangesResolving = newPendingReq,
                    () => this.pendingExchangesResolving = undefined
                );
        };

        return this.activeExchangesCache
            ? of(this.activeExchangesCache)
            : getPendingRequest().request$;
    }

    private fetchExchangeRecs(): Observable<ExchangeRec[]> {
        let isDefaultExchanges: boolean = false;

        const defaultExchangeRecs$ = defer<Observable<ExchangeRec[]>>(() => {
            isDefaultExchanges = true;

            return of(
                (Object.keys(EXCHANGE_NAMING) as Exchange[])
                    .map<ExchangeRec>(exchange => ({
                        key:    exchange,
                        name:   EXCHANGE_NAMING[exchange],
                        active: true
                    }))
            );
        });

        const exchangeRecs$ = this.isExchangesApiAvailable()
            ? this.httpClient.get<ExchangeRec[]>(this.exchangesUrl).pipe(
                catchError(handleResponseError),
                catchError(() => defaultExchangeRecs$)
            )
            : defaultExchangeRecs$;

        return exchangeRecs$.pipe(
            tap(recs => {
                this.cacheExchanges(recs);

                if (!isDefaultExchanges) {
                    this.patchExchangeNaming(recs);
                }
            })
        );
    }

    private cacheExchanges(exchangeRecs: ExchangeRec[]): void {
        this.activeExchangesCache = exchangeRecs.reduce(
            (acc, rec) => {
                if (rec.active) {
                    acc.push(rec.key);
                }

                return acc;
            },
            [] as Exchange[]
        );
    }

    private patchExchangeNaming(exchangeRecs: ExchangeRec[]): void {

        interface OutdatedExchangeRec {
            key: Exchange;
            name: string;
        }

        interface RenamedExchangeRec extends ExchangeRec {
            oldName: string;
        }

        const missingExchanges: ExchangeRec[] = [];
        const renamedExchanges: RenamedExchangeRec[] = [];

        const outdatedExchangeNaming = {...EXCHANGE_NAMING};
        delete outdatedExchangeNaming[Exchange.AUTOSPREADER];
        delete outdatedExchangeNaming[Exchange.SMART];
        delete outdatedExchangeNaming[SMART_ACCOUNT_EXCHANGE_API_VALUE];

        exchangeRecs.forEach(rec => {
            const oldName = EXCHANGE_NAMING[rec.key];

            if (!oldName) {
                missingExchanges.push(rec);
            } else if (oldName !== rec.name) {
                renamedExchanges.push({
                    ...rec,
                    oldName
                });
            }

            delete outdatedExchangeNaming[rec.key];

            EXCHANGE_NAMING[rec.key] = rec.name;
        });

        const outdatedExchanges: OutdatedExchangeRec[] = (Object.keys(outdatedExchangeNaming) as Exchange[])
            .reduce(
                (acc, exchange) => {
                    acc.push({
                        key:  exchange,
                        name: outdatedExchangeNaming[exchange]
                    });

                    return acc;
                },
                [] as OutdatedExchangeRec[]
            );

        if (outdatedExchanges.length || missingExchanges.length || renamedExchanges.length) {
            console.warn('Exchanges dictionary requires update!', {
                outdatedExchanges,
                missingExchanges,
                renamedExchanges
            });
        }
    }

}
